import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FormBuilder from '../components/FormBuilder';
import DynamicForm from '../components/DynamicForm';
import { API_BASE_URL } from '../config';

const EventDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Registration form state
  const [registration, setRegistration] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Default Proposal form state
  const [proposal, setProposal] = useState({
    name: '',
    email: '',
    title: '',
    abstract: '',
    bio: ''
  });

  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [proposalSuccess, setProposalSuccess] = useState(null);

  // Custom form states
  const [customForm, setCustomForm] = useState(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [customFormSubmission, setCustomFormSubmission] = useState(null);

  // Load event details
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/event/${id}`);
        
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
        } else {
          setError('Event not found');
        }
      } catch (error) {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEvent();
    }
  }, [id]);

  // Load custom form
  const loadCustomForm = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/event/${id}/custom-forms/cfp`);
      if (response.ok) {
        const formData = await response.json();
        setCustomForm(formData);
      } else {
        setCustomForm(null);
      }
    } catch (error) {
      console.log('No custom form found');
      setCustomForm(null);
    }
  };

  useEffect(() => {
    if (id) {
      loadCustomForm();
    }
  }, [id]);

  const handleRegistration = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/event/${id}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registration),
      });

      const data = await response.json();
      
      if (response.ok) {
        setRegistrationSuccess(data);
        setRegistration({ name: '', email: '', phone: '' });
      } else {
        setRegistrationSuccess({ error: data.message });
      }
    } catch (error) {
      console.error('Error registering:', error);
      setRegistrationSuccess({ error: 'Network error. Please try again.' });
    }
  };

  const handleProposalSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/event/${id}/call-for-papers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(proposal),
      });

      const data = await response.json();
      
      if (response.ok) {
        setProposalSuccess(data);
        setProposal({ name: '', email: '', title: '', abstract: '', bio: '' });
      } else {
        setProposalSuccess({ error: data.message });
      }
    } catch (error) {
      console.error('Error submitting proposal:', error);
      setProposalSuccess({ error: 'Network error. Please try again.' });
    }
  };

  const handleCustomFormSubmit = async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/custom-forms/${customForm.id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submission_data: formData,
          submitted_by: formData.speaker_name || formData.name || 'Anonymous',
          submitted_email: formData.email || ''
        })
      });

      const data = await response.json();

      if (response.ok) {
        setCustomFormSubmission({
          success: true,
          message: 'Your proposal has been submitted successfully!',
          data: data
        });
      } else {
        setCustomFormSubmission({
          success: false,
          error: data.message || 'Failed to submit proposal'
        });
      }
    } catch (error) {
      console.error('Error submitting custom form:', error);
      setCustomFormSubmission({
        success: false,
        error: 'Network error. Please try again.'
      });
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Loading Event Details...</h2>
        <p>Please wait while we load the event information.</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Event Not Found</h2>
        <p>{error || 'The event you are looking for does not exist.'}</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Back to Home
        </button>
      </div>
    );
  }

  // Check if current user is the organizer
  const isOrganizer = user && event && user.id === event.organizer_id;

  return (
    <div className="event-detail">
      <div className="event-header event-overlay">
        {event.banner ? (
          <img 
            src={`https://eventfork.com/uploads/${event.banner}`} 
            alt={event.name}
            className="event-banner"
          />
        ) : (
          <div className="event-banner-placeholder" style={{height: '400px', display: 'flex'}}>
            <span>{event.name}</span>
          </div>
        )}
        <div className="event-header-content">
          <h1>{event.name}</h1>
          <p className="event-meta">
            {new Date(event.schedule).toLocaleDateString()} • {event.venue} • Organized by {event.organization || event.organizer_name}
          </p>
        </div>
      </div>

<div className='left-event-bar'>
      <div className="event-tabs">
        <button 
          className={activeTab === 'details' ? 'active' : ''}
          onClick={() => setActiveTab('details')}
        >
          📋 Event Details
        </button>
        <button 
          className={activeTab === 'register' ? 'active' : ''}
          onClick={() => setActiveTab('register')}
        >
          📝 Register
        </button>
        <button 
          className={activeTab === 'cfp' ? 'active' : ''}
          onClick={() => setActiveTab('cfp')}
        >
          🎤 Call for Papers
        </button>
      </div>

      <div className="event-content">
        {activeTab === 'details' && (
          <div className="event-description">
            <h2>About This Event</h2>
            <div style={{ 
              background: '#f8f9fa', 
              padding: '2rem', 
              borderRadius: '8px',
              marginBottom: '2rem'
            }}>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                {event.description || 'No detailed description available for this event.'}
              </p>
            </div>
            
            <div className="event-info">
              <h3>Event Information</h3>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <p><strong>📅 Date & Time:</strong> {new Date(event.schedule).toLocaleString()}</p>
                <p><strong>📍 Venue:</strong> {event.venue}</p>
                <p><strong>👨‍💼 Organizer:</strong> {event.organization || event.organizer_name}</p>
                <p><strong>📅 Created:</strong> {new Date(event.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <button 
                onClick={() => setActiveTab('register')}
                className="btn btn-primary"
                style={{ marginRight: '1rem' }}
              >
                Register for this Event
              </button>
              <button 
                onClick={() => setActiveTab('cfp')}
                className="btn btn-secondary"
              >
                Submit a Talk Proposal
              </button>
            </div>
          </div>
        )}

        {activeTab === 'register' && (
          <div className="registration-form">
            <h2>Register for "{event.name}"</h2>
            <p style={{ marginBottom: '2rem', color: '#666' }}>
              Fill out the form below to register for this event. You'll receive a confirmation with a QR code for event entry.
            </p>
            
            {registrationSuccess ? (
              registrationSuccess.error ? (
                <div className="error-message">
                  <h3>❌ Registration Failed</h3>
                  <p>{registrationSuccess.error}</p>
                  <button 
                    onClick={() => setRegistrationSuccess(null)} 
                    className="btn btn-primary"
                    style={{ marginTop: '1rem' }}
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <div className="success-message">
                  <h3>✅ Registration Successful!</h3>
                  <p>Thank you for registering for <strong>{event.name}</strong>.</p>
                  <p>A confirmation has been sent to <strong>{registrationSuccess.registration?.email}</strong></p>
                  
                  {registrationSuccess.registration?.qrCode && (
                    <div className="qr-code">
                      <h4>Your Event Pass</h4>
                      <img src={registrationSuccess.registration.qrCode} alt="Registration QR Code" />
                      <p><strong>Show this QR code at the event entrance</strong></p>
                    </div>
                  )}
                  
                  <div style={{ marginTop: '2rem' }}>
                    <button 
                      onClick={() => setRegistrationSuccess(null)} 
                      className="btn btn-primary"
                    >
                      Register Another Person
                    </button>
                    <button 
                      onClick={() => setActiveTab('details')} 
                      className="btn btn-secondary"
                      style={{ marginLeft: '1rem' }}
                    >
                      Back to Event Details
                    </button>
                  </div>
                </div>
              )
            ) : (
              <form onSubmit={handleRegistration} style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={registration.name}
                    onChange={(e) => setRegistration({ ...registration, name: e.target.value })}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    value={registration.email}
                    onChange={(e) => setRegistration({ ...registration, email: e.target.value })}
                    required
                    placeholder="Enter your email address"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    value={registration.phone}
                    onChange={(e) => setRegistration({ ...registration, phone: e.target.value })}
                    required
                    placeholder="Enter your phone number"
                  />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '1rem' }}>
                  Register Now
                </button>
                
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button 
                    type="button"
                    onClick={() => setActiveTab('details')} 
                    className="btn btn-secondary"
                  >
                    Back to Event Details
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {activeTab === 'cfp' && (
          <div className="proposal-form">
            {/* Organizer Form Builder Section */}
            {isOrganizer && (
              <div className="organizer-actions">
                <h3>🎨 Organizer Tools</h3>
                <button 
                  onClick={() => setShowFormBuilder(!showFormBuilder)}
                  className="btn btn-secondary"
                >
                  {showFormBuilder ? '❌ Hide Form Builder' : '🏗️ Create Custom CFP Form'}
                </button>
                <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                  Create a custom submission form with your own fields and requirements.
                </p>
                
                {showFormBuilder && (
                  <FormBuilder 
                    eventId={id} 
                    formType="cfp" 
                    onFormCreated={() => {
                      setShowFormBuilder(false);
                      loadCustomForm();
                    }} 
                  />
                )}
              </div>
            )}

            {/* Custom Form Submission Result */}
            {customFormSubmission && (
              <div className={customFormSubmission.success ? 'success-message' : 'error-message'}>
                <h3>{customFormSubmission.success ? '✅ Submission Successful!' : '❌ Submission Failed'}</h3>
                <p>{customFormSubmission.success ? customFormSubmission.message : customFormSubmission.error}</p>
                <button 
                  onClick={() => setCustomFormSubmission(null)}
                  className="btn btn-primary"
                >
                  {customFormSubmission.success ? 'Submit Another Proposal' : 'Try Again'}
                </button>
              </div>
            )}

            {/* Custom Form or Default Form */}
            {customForm && !customFormSubmission ? (
              <div className="custom-form-section">
                <div className="custom-form-header">
                  <h2>🎤 {customForm.form_title}</h2>
                  {customForm.form_description && (
                    <p className="form-description">{customForm.form_description}</p>
                  )}
                  {isOrganizer && (
                    <div className="organizer-notice">
                      <small>🔧 This is your custom form. Speakers will see this instead of the default form.</small>
                    </div>
                  )}
                </div>
                
                <DynamicForm 
                  formConfig={customForm}
                  onSubmit={handleCustomFormSubmit}
                />
              </div>
            ) : !customFormSubmission && (
              /* Default CFP Form */
              <div className="default-form-section">
                <h2>Submit a Talk Proposal for "{event.name}"</h2>
                <p style={{ marginBottom: '2rem', color: '#666' }}>
                  Interested in speaking at this event? Submit your talk proposal below.
                </p>
                
                {proposalSuccess ? (
                  proposalSuccess.error ? (
                    <div className="error-message">
                      <h3>❌ Submission Failed</h3>
                      <p>{proposalSuccess.error}</p>
                      <button 
                        onClick={() => setProposalSuccess(null)} 
                        className="btn btn-primary"
                        style={{ marginTop: '1rem' }}
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <div className="success-message">
                      <h3>✅ Proposal Submitted Successfully!</h3>
                      <p>Thank you for submitting your talk proposal for <strong>{event.name}</strong>.</p>
                      <p>The organizers will review your proposal and contact you soon.</p>
                      
                      <div style={{ marginTop: '2rem' }}>
                        <button 
                          onClick={() => setProposalSuccess(null)} 
                          className="btn btn-primary"
                        >
                          Submit Another Proposal
                        </button>
                        <button 
                          onClick={() => setActiveTab('details')} 
                          className="btn btn-secondary"
                          style={{ marginLeft: '1rem' }}
                        >
                          Back to Event Details
                        </button>
                      </div>
                    </div>
                  )
                ) : (
                  <form onSubmit={handleProposalSubmit} style={{ maxWidth: '600px', margin: '0 auto' }}>
                    <div className="form-group">
                      <label htmlFor="cfp-name">Full Name *</label>
                      <input
                        type="text"
                        id="cfp-name"
                        value={proposal.name}
                        onChange={(e) => setProposal({ ...proposal, name: e.target.value })}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="cfp-email">Email Address *</label>
                      <input
                        type="email"
                        id="cfp-email"
                        value={proposal.email}
                        onChange={(e) => setProposal({ ...proposal, email: e.target.value })}
                        required
                        placeholder="Enter your email address"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="cfp-title">Talk Topic *</label>
                      <input
                        type="text"
                        id="cfp-title"
                        value={proposal.title}
                        onChange={(e) => setProposal({ ...proposal, title: e.target.value })}
                        required
                        placeholder="Enter your talk topic"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="cfp-abstract">Abstract *</label>
                      <textarea
                        id="cfp-abstract"
                        value={proposal.abstract}
                        onChange={(e) => setProposal({ ...proposal, abstract: e.target.value })}
                        rows="6"
                        required
                        placeholder="Describe your talk abstract, what attendees will learn, key takeaways..."
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label htmlFor="cfp-bio">Speaker Bio</label>
                      <textarea
                        id="cfp-bio"
                        value={proposal.bio}
                        onChange={(e) => setProposal({ ...proposal, bio: e.target.value })}
                        rows="4"
                        placeholder="Tell us about your background, experience, and expertise..."
                      ></textarea>
                    </div>
                    <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '1rem' }}>
                      Submit Proposal
                    </button>
                    
                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                      <button 
                        type="button"
                        onClick={() => setActiveTab('details')} 
                        className="btn btn-secondary"
                      >
                        Back to Event Details
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default EventDetail;
